/**
 * Fictional sample investment memo. Company, people and figures are invented
 * to illustrate the format. Replace with an approved, anonymized real report when available.
 */

export type CriterionStatus = 'met' | 'partial' | 'not-met' | 'unknown'

export const SAMPLE_MEMO = {
  label: 'Illustrative example — fictional company',
  company: 'Tallyfox',
  oneLiner: 'Accounts-payable automation for mid-market finance teams',
  meta: ['Series A', 'B2B fintech', 'Toronto, Canada'],
  preparedFor: 'Sample fund thesis: B2B software · Seed–Series A · North America',

  overview:
    'Tallyfox automates invoice capture, approval routing and payment scheduling for finance teams at companies with 200–2,000 employees. It sells per-seat subscriptions to controllers and CFOs, and positions itself against manual processes and legacy ERP add-ons.',

  thesisFit: {
    summary: '4 of 6 criteria met · 1 partial · 1 unknown',
    note: 'Fit reflects the sample fund’s criteria and the information provided. It is not an investment recommendation.',
    criteria: [
      { label: 'B2B software', status: 'met' as CriterionStatus, detail: 'Vertical SaaS for finance teams', source: 'Deck, slide 3' },
      { label: 'Seed–Series A', status: 'met' as CriterionStatus, detail: 'Raising Series A', source: 'Deck, slide 14' },
      { label: 'North America', status: 'met' as CriterionStatus, detail: 'HQ Toronto; customers in CA and US', source: 'Deck, slide 9' },
      { label: 'ARR above $2M', status: 'met' as CriterionStatus, detail: '$3.1M reported', source: 'Deck, slide 8' },
      { label: 'Net revenue retention above 110%', status: 'unknown' as CriterionStatus, detail: 'Not provided', source: '—' },
      { label: 'Capital-efficient growth', status: 'partial' as CriterionStatus, detail: 'Burn multiple ~1.9× on stated figures', source: 'Financial model, tab 2' },
    ],
  },

  strengths: [
    { text: 'CEO previously ran AP operations at a mid-market manufacturer — direct exposure to the customer problem.', source: 'Deck, slide 12' },
    { text: 'Reported ARR grew from $1.4M to $3.1M over the four quarters shown.', source: 'Deck, slide 8 · Revenue schedule' },
    { text: 'No single customer exceeds 6% of revenue in the customer list provided.', source: 'Data room: Customer_List' },
  ],

  risks: [
    { text: 'ERP vendors are bundling AP automation into existing suites.', source: 'Competitor research' },
    { text: 'Payments depend on one partner; the agreement is referenced but not in the data room.', source: 'Data room: Board_Minutes_Q2' },
    { text: 'Deck ARR differs from the latest month annualized in the revenue schedule.', source: 'Deck slide 8 vs. Revenue schedule' },
  ],

  openQuestions: [
    'What is net revenue retention by annual cohort?',
    'Can we review the payment-partner agreement and its termination terms?',
    'How is ARR defined, and what explains the gap with the revenue schedule?',
    'What sales leadership is planned for the move upmarket?',
  ],

  financials: [
    { label: 'Reported ARR', value: '$3.1M', note: 'Company-provided' },
    { label: 'Gross margin', value: '74%', note: 'As reported' },
    { label: 'Net burn / month', value: '$240K', note: 'Trailing 3 months' },
    { label: 'Implied runway', value: '14 months', note: 'Cash ÷ net burn' },
  ],

  sources: [
    'Pitch deck (14 slides)',
    'Financial model (3 tabs)',
    'Revenue schedule',
    'Customer list',
    'Board minutes, Q2',
    'Public competitor information',
  ],

  analystJudgment: [
    'Whether the market and team justify the price',
    'How much weight to give unverified company figures',
    'Reference and customer calls',
    'The investment recommendation to IC',
  ],
} as const

export const MEMO_EXPLAINER = [
  {
    title: 'What went in',
    body: 'A pitch deck, a financial model, selected data room documents and the fund’s investment criteria.',
  },
  {
    title: 'What the memo contains',
    body: 'Company overview, thesis fit by criterion, strengths and risks with sources, open questions and a financial snapshot.',
  },
  {
    title: 'What still needs your team',
    body: 'Verifying company figures, reference calls, judgment on price and the recommendation itself.',
  },
] as const
